import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import { DoHttpService } from 'src/app/core/services/do-http.service';
import { LayoutService } from 'src/app/core/services/layout.service';
import { LiteProjectRealtimeService } from 'src/app/core/services/realtime-services/lite-project-realtime.service';
import { UserDetailService } from 'src/app/core/services/user-detail.service';
import { ApiUrls } from 'src/app/shared/constants/ApiUrls';
import { GridStatus } from 'src/app/shared/enum/GridStatus';
import { ProductTypeEnum, SketchTypes } from 'src/app/shared/enum/ProductTypeEnum';
import { LiteComponentType, LiteProject } from 'src/app/shared/interfaces/DrawoutLite/LiteProject';
import { LiteControllerIOView, LiteSchematicSelectionChange, LiteSchematicTemplatePreview } from 'src/app/shared/interfaces/DrawoutLite/LiteProjectUI';

const defaultPreviewValue: LiteSchematicTemplatePreview = {
  template: null,
  pageCaseOptions: [],
  caseConfigurations: null,
  showingDefault: false,
  componentType: new LiteComponentType,
  noDataText: 'Select a template to preview',
  showCategoryImages: false,
  categoryImages: [],
  categoryId: null,
  subCategoryName: null,
  subCategories: [],
  isSeries: false
}

@Injectable({
  providedIn: 'root'
})
export class DrawoutLiteService {

  sketchType: number;
  availableSketchApps: number[] = [];
  appsToBePurchased: number[] = [];

  public projectDashboardStructure = new BehaviorSubject<LiteSchematicSelectionChange>({
    selectedSchematicId: null,
    showListingScreen: true
  });
  public templatePreview = new BehaviorSubject<LiteSchematicTemplatePreview>(defaultPreviewValue);
  public templatePreviewChanged = new Subject();
  public assignableControllerPreview = new BehaviorSubject<LiteSchematicTemplatePreview>(defaultPreviewValue);
  public assignableControllerPreviewChanged = new Subject();
  public selectedControllerIOId = new BehaviorSubject<LiteControllerIOView>(null);
  public reloadSchematicList = new Subject<boolean>();
  public appsLoaded = new Subject<void>();

  public projectId: string;
  public cadId: number;

  public project: LiteProject;
  public projectEdited = new Subject<boolean>();

  isReordering = false;

  constructor(
    private liteRTService: LiteProjectRealtimeService,
    private layoutService: LayoutService,
    private userDetailService: UserDetailService,
    private request: DoHttpService,
  ) {
    this.layoutService.isSketchAvailable.subscribe(res => {
      if (res) {
        this.loadAvailableSketchApps();
      }
    })
  }

  get selectedSketchType() {
    return SketchTypes.find(item => item.id === this.sketchType).sketchType;
  }

  get selectedCAD() {
    return SketchTypes.find(item => item.id === this.sketchType).cadId;
  }

  loadAvailableSketchApps() {
    const allSketchApps = [
      ProductTypeEnum.EplanSketch,
      ProductTypeEnum.PCSSketch,
      ProductTypeEnum.SEESketch,
      ProductTypeEnum.EplanCTSSketch,
      ProductTypeEnum.SEECTSSketch,
      ProductTypeEnum.PCSCTSSketch
    ]
    this.request.get(ApiUrls.getAvailableSketchApps).subscribe(res => {
      const availableOrganisationProducts = this.userDetailService.getUserData().organisationProducts;
      if (res.length) {
        this.availableSketchApps = res.map(item => item.productId);
        this.appsToBePurchased = allSketchApps.filter(item => {
          if (!this.availableSketchApps.includes(item) && !availableOrganisationProducts.includes(item)) {
            return item;
          }
        })
        this.removeCTSItems();
      } else {
        this.availableSketchApps = [];
        this.appsToBePurchased = [...allSketchApps].filter(item => !availableOrganisationProducts.includes(item));
        this.removeCTSItems();
      }
      this.appsLoaded.next();
    });
  }

  removeCTSItems() {
    const ctsItems = [ProductTypeEnum.PCSCTSSketch, ProductTypeEnum.EplanCTSSketch, ProductTypeEnum.SEECTSSketch]
    this.appsToBePurchased = this.appsToBePurchased.filter(item => !ctsItems.includes(item))
  }


  public addTemplatePreview(addingSchematic: LiteSchematicTemplatePreview) {
    if (addingSchematic === null) {
      this.templatePreview.next(defaultPreviewValue);
    } else {
      this.templatePreview.next(addingSchematic);
    }
  }

  public addAssignableControllerPreview(addingSchematic: LiteSchematicTemplatePreview) {
    if (addingSchematic === null) {
      this.assignableControllerPreview.next(defaultPreviewValue);
    } else {
      this.assignableControllerPreview.next(addingSchematic);
    }
  }

  toggleSchematicReordering(isReordering?: boolean) {
    this.isReordering = isReordering ?? !this.isReordering;
  }

  public cleanPreviewData() {
    this.addTemplatePreview(null);
  }

  public cleanDashboardStructure() {
    this.projectDashboardStructure.next({
      selectedSchematicId: null,
      showListingScreen: true
    });
  }

  public cleanProjectData() {
    this.cleanPreviewData();
    this.selectedControllerIOId.next(null);
    this.cleanDashboardStructure();
  }

  public get disableChanges() {
    return this.liteRTService.ProjectDataChanges.pipe(map((data) => {
      return data?.currentValue.generationStatus === GridStatus.Submitted;
    }));
  }

  public get projectData() {
    return this.liteRTService.ProjectDataChanges.pipe(map((data) => {
      return data?.currentValue;
    }));
  }

  public get projectProgress() {
    return this.liteRTService.ProjectDataChanges.pipe(map((data) => {
      return data?.currentValue.generationProgress;
    }));
  }

  public get enableDownloadButton() {
    return this.liteRTService.ProjectDataChanges.pipe(map((data) => {
      const currentValue = data?.currentValue;
      return [GridStatus.Submitted, GridStatus.InQueue, GridStatus.Success, GridStatus.Processing].includes(currentValue.generationStatus);
    }));
  }

}
